import React from 'react'

import { T, VStack } from 'govwell-ui'
import { ViewDataElementFragment, TimeZone } from 'types/graphql'

import Tag, { TagColor } from 'src/components/Tag'
import { DateFormats, formatDateInTimeZone } from 'src/utils/date'

// Must stay in sync with ImportClaimStatus in api/src/services/importLicenseClaims/importClaimStatus.ts
const CLAIM_STATUS = {
  Claimed: 'Claimed',
  ClaimPendingReview: 'Claims Pending Review',
  ImportedUnclaimed: 'Imported & Unclaimed',
} as const

type ImportClaimStatusLabel = (typeof CLAIM_STATUS)[keyof typeof CLAIM_STATUS]

const importClaimStatusLabelValues = new Set<string>(Object.values(CLAIM_STATUS))

const isImportClaimStatusLabel = (
  value: string | null | undefined
): value is ImportClaimStatusLabel => value != null && importClaimStatusLabelValues.has(value)

type Props = {
  data: ViewDataElementFragment[]
  timeZone: TimeZone | null | undefined
}

const ClaimStatusCell = ({ data, timeZone }: Props) => {
  const statusElement = data[0]
  const expirationElement = data[1]

  if (!statusElement || statusElement.__typename !== 'ViewDataElementString') {
    return <T.Body>-</T.Body>
  }

  const statusLabel = statusElement.string
  const tagColor = isImportClaimStatusLabel(statusLabel) ? getTagColor(statusLabel) : 'blue'

  const showExpiration =
    expirationElement &&
    expirationElement.__typename === 'ViewDataElementDateTime' &&
    statusLabel !== CLAIM_STATUS.Claimed

  const isExpired = showExpiration && new Date(expirationElement.dateTime) < new Date()

  if (!showExpiration) {
    return (
      <span aria-label={`Claim status: ${statusLabel}`}>
        <Tag color={tagColor}>{statusLabel}</Tag>
      </span>
    )
  }

  return (
    <VStack gap={1}>
      <span aria-label={`Claim status: ${statusLabel}`}>
        <Tag color={tagColor}>{statusLabel}</Tag>
      </span>
      <T.BodySmall className={isExpired ? 'text-red-600' : 'text-gray-500'}>
        {isExpired ? 'Code expired on ' : 'Code expires on '}
        {formatDateInTimeZone(
          expirationElement.dateTime,
          timeZone,
          DateFormats.MonthDayFullYearWithSlash
        )}
      </T.BodySmall>
    </VStack>
  )
}

const getTagColor = (status: ImportClaimStatusLabel): TagColor => {
  switch (status) {
    case CLAIM_STATUS.Claimed:
      return 'green'
    case CLAIM_STATUS.ClaimPendingReview:
      return 'yellow'
    case CLAIM_STATUS.ImportedUnclaimed:
      return 'blue'
  }
}

export default React.memo(ClaimStatusCell)
